// React
import { useState } from 'react'

// Mantine
import { Button, SegmentedControl, Modal, Select } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'

// Hooks
import { useLineItems } from '@/hooks/useLineItems'
import { useComponentsAvailability } from '@/hooks/useComponentsAvailability'

// UI Components
import LocationSelect from '@/components/ui/LocationSelect'
import AddressForm from '@/components/ui/AddressForm'
import UiBox from '@/components/ui/Box'
import LineItemsEditor from '@/components/lines/LineItemsEditor'
import ComponentsAvailabilityChecker from '@/components/ui/ComponentsAvailabilityChecker'
import Banner from '@/components/ui/Banner'

/**
 * Form for creating/editing fulfillment orders
 */
export default function FulfillmentOrderForm({
    fulfillment_order = null,
    initialConsignee = null,
    initialLocationId = null,
    initialIncoterm = 'DAP',
    initialWarrantiesMode = 'blank',
    onSubmit,
}) {
    // Location state (source location)
    const [locationId, setLocationId] = useState(
        fulfillment_order?.location_id
            ? String(fulfillment_order.location_id)
            : initialLocationId
              ? String(initialLocationId)
              : '1'
    )

    // Recipient state
    const [recipientType, setRecipientType] = useState('address') // 'address' or 'location'
    const [toLocationId, setToLocationId] = useState(null)
    const [consignee, setConsignee] = useState(
        fulfillment_order?.consignee || initialConsignee || {
            name: '',
            email: '',
            phone: '',
            company: '',
            address1: '',
            address2: '',
            city: '',
            zip: '',
            country_code: '',
        }
    )
    const [addressModalOpened, setAddressModalOpened] = useState(false)

    // Fulfillment options
    const [fulfillAt, setFulfillAt] = useState(new Date())
    const [warrantiesMode, setWarrantiesMode] = useState(initialWarrantiesMode)
    const [incoterm, setIncoterm] = useState(initialIncoterm)

    // Use the line items hook
    const {
        lines,
        loading,
        addComponent,
        addVariant,
        updateLinePrice,
        updateLineQuantity,
        removeLine,
        total_without_shipping,
        total_quantity,
    } = useLineItems(fulfillment_order?.lines)

    // Availability check - prepare lines for the hook
    const availabilityLines = lines.map((line) => ({
        ...(line.shopify_product_variant_id
            ? { variant_id: line.shopify_product_variant_id }
            : { component_id: line.component_id }),
        quantity: line.quantity,
    }))

    const {
        isVariantAvailable,
        result: availabilityResult,
        getComponentStatus,
    } = useComponentsAvailability({
        locationId: Number(locationId),
        lines: availabilityLines,
        isSimulation: true,
    })

    // Recipient validation
    const isRecipientValid = (() => {
        if (recipientType === 'address') {
            const hasIdentity =
                consignee.name?.trim() || consignee.company?.trim()
            const hasAddress =
                consignee.address1?.trim() &&
                consignee.city?.trim() &&
                consignee.country_code?.trim()
            return !!(hasIdentity && hasAddress)
        }
        // recipientType === 'location'
        return toLocationId && toLocationId !== locationId
    })()

    // Check if a line has availability issues
    const isLineAvailable = (line) => {
        if (line.shopify_product_variant_id) {
            return isVariantAvailable(line.shopify_product_variant_id)
        }
        if (line.component_id && availabilityResult?.components) {
            const comp = availabilityResult.components.find(
                (c) => c.id === line.component_id
            )
            return !comp || getComponentStatus(comp) === 'available'
        }
        return true
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        // Convert normalized lines to database format
        const linesOutput = lines.map((line) => ({
            variant_id: line.shopify_product_variant_id || null,
            component_id: line.component_id || null,
            total_quantity: line.quantity,
            remaining_quantity: line.quantity,
            unit_price_initial: line.unit_price,
        }))

        // Format date as YYYY-MM-DD
        const formatDate = (date) => {
            if (!date) return null
            return date.toISOString().split('T')[0]
        }

        // Build output object matching database schema
        const output = {
            location_id: locationId,
            to_location_id: recipientType === 'location' ? toLocationId : null,
            consignee: recipientType === 'address' ? consignee : null,
            fulfill_at: formatDate(fulfillAt),
            warranties_mode: warrantiesMode,
            incoterm,
            lines: linesOutput,
        }

        onSubmit(output)
    }

    const handleAddressSubmit = (values) => {
        setConsignee(values)
        setAddressModalOpened(false)
    }

    return (
        <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5 max-w-[800px] mx-auto"
        >
            {/* Location Section - First */}
            <UiBox title="Lieu de préparation">
                <LocationSelect
                    label="Lieu de préparation"
                    placeholder="Choisir un lieu de préparation..."
                    value={locationId}
                    onChange={setLocationId}
                />
            </UiBox>

            {/* Recipient Section */}
            <UiBox title="Destinataire">
                <div className="flex flex-col gap-4">
                    {/* Recipient type selector */}
                    <SegmentedControl
                        value={recipientType}
                        onChange={setRecipientType}
                        data={[
                            { label: 'Adresse manuelle', value: 'address' },
                            { label: 'Emplacement interne', value: 'location' },
                        ]}
                    />

                    {/* Conditional rendering based on recipient type */}
                    {recipientType === 'address' ? (
                        <div className="flex flex-col gap-3">
                            {/* Display address */}
                            <div className="flex flex-col p-4 bg-gray-50 rounded-lg text-sm">
                                {consignee.name && (
                                    <span className="font-medium">
                                        {consignee.name}
                                    </span>
                                )}
                                {consignee.company && (
                                    <span className="text-gray-600">
                                        {consignee.company}
                                    </span>
                                )}
                                {consignee.email && (
                                    <span>{consignee.email}</span>
                                )}
                                {consignee.phone && (
                                    <span>{consignee.phone}</span>
                                )}
                                {consignee.address1 && (
                                    <span className="mt-2">
                                        {consignee.address1}
                                    </span>
                                )}
                                {consignee.address2 && (
                                    <span>{consignee.address2}</span>
                                )}
                                {(consignee.zip || consignee.city) && (
                                    <span>
                                        {consignee.zip} {consignee.city}
                                    </span>
                                )}
                                {consignee.country_code && (
                                    <span>{consignee.country_code}</span>
                                )}

                                {!consignee.name && !consignee.address1 && (
                                    <span className="text-gray-400 italic">
                                        Aucune adresse définie
                                    </span>
                                )}
                            </div>

                            {/* Modal for editing address */}
                            <Modal
                                title="Modifier l'adresse du destinataire"
                                opened={addressModalOpened}
                                onClose={() => setAddressModalOpened(false)}
                                size="lg"
                            >
                                <AddressForm
                                    initialValues={consignee}
                                    onSubmit={handleAddressSubmit}
                                />
                            </Modal>

                            {/* Button to open modal */}
                            <Button
                                variant="default"
                                onClick={() => setAddressModalOpened(true)}
                            >
                                Modifier l'adresse
                            </Button>
                        </div>
                    ) : (
                        <>
                            <LocationSelect
                                label="Emplacement de destination"
                                placeholder="Choisir un emplacement..."
                                value={toLocationId}
                                onChange={setToLocationId}
                            />
                            {toLocationId && toLocationId === locationId && (
                                <Banner
                                    type="error"
                                    compact={true}
                                    title="Le destinataire doit être différent du lieu de préparation"
                                />
                            )}
                        </>
                    )}
                </div>
            </UiBox>

            {/* Products Section */}
            <LineItemsEditor
                lines={lines}
                addComponent={addComponent}
                addVariant={addVariant}
                updateLinePrice={updateLinePrice}
                updateLineQuantity={updateLineQuantity}
                removeLine={removeLine}
                total_without_shipping={total_without_shipping}
                total_quantity={total_quantity}
                isLineAvailable={isLineAvailable}
            >
                {lines.length > 0 && (
                    <ComponentsAvailabilityChecker
                        isSimulation={true}
                        locationId={Number(locationId)}
                        lines={lines.map((line) => ({
                            ...(line.shopify_product_variant_id
                                ? {
                                      variant_id:
                                          line.shopify_product_variant_id,
                                  }
                                : { component_id: line.component_id }),
                            quantity: line.quantity,
                        }))}
                    />
                )}
            </LineItemsEditor>

            {/* Options Section */}
            <UiBox title="Options">
                <div className="flex flex-col gap-4">
                    <DatePickerInput
                        label="Date de préparation"
                        value={fulfillAt}
                        onChange={setFulfillAt}
                        locale="fr"
                        valueFormat="LL"
                        minDate={new Date()}
                    />
                    <Select
                        label="Déclaration douanière"
                        description="En cas de passage de douane, définit qui paie les droits"
                        allowDeselect={false}
                        value={incoterm}
                        onChange={setIncoterm}
                        data={[
                            {
                                value: 'DAP',
                                label: 'Le destinataire paie les droits de douane (DAP)',
                            },
                            {
                                value: 'DDP',
                                label: 'Droits de douane prépayés (DDP)',
                            },
                        ]}
                    />
                    <Select
                        label="Cartes de garanties"
                        allowDeselect={false}
                        value={warrantiesMode}
                        onChange={setWarrantiesMode}
                        data={[
                            {
                                value: 'associate',
                                label: 'Associer',
                            },
                            {
                                value: 'blank',
                                label: 'Cartes vierges',
                            },
                            {
                                value: 'none',
                                label: 'Pas de cartes',
                            },
                        ]}
                    />
                </div>
            </UiBox>

            <Button
                type="submit"
                disabled={lines.length === 0 || !isRecipientValid}
                loading={loading}
            >
                Enregistrer
            </Button>
        </form>
    )
}
